import type {
  Product,
  Pagination,
  CollectionDto,
  CollectionPack,
  CollectionProduct,
  CollectionFilters,
  CollectionFilterDefinition,
  FacetValue,
  CollectionPageResponse,
  HomePageResponse,
  GlobalChromeResponse,
  ProductPageResponse,
  PackPageResponse,
  PackSummaryResponse,
  PricingContext,
  Review,
  ReviewStats,
  ReviewFilters,
  CreateReviewInput,
  QuoteItem,
  QuoteResult,
  BatchResolveItem,
  BatchResolveResult,
  ResolveProductPriceResult,
  CreditsConfig,
  CreditsBalance,
  CreditsTransactions,
  CustomerCoupons,
  Cart,
  CreateCartInput,
  UpdateCartInput,
  AddPackToCartInput,
  RemoveCartItemInput,
  AuthResponse,
  UserProfile,
  LoginRequest,
  MagicLinkRequest,
  MagicLinkRequestResponse,
  MagicLinkVerifyRequest,
  MagicLinkVerifyResponse,
  UpdateProfileRequest,
  RegisterRequest,
  GoogleAuthRequest,
  ShippingAddress,
  CreateShippingAddressInput,
  UpdateShippingAddressInput,
  CreatePaymentMethodRequest,
  PaymentMethodResponse,
  Order,
  OrderPagination,
  RefundOrderRequest,
  CustomerSubscription,
  SubscriptionItemUpdate,
} from './types';

export interface StorefrontClientOptions {
  baseUrl: string;
  tenantId: string;
  headers?: Record<string, string>;
}

export class StorefrontBffError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'StorefrontBffError';
    this.status = status;
    this.code = code;
  }
}

function throwStorefrontBffError(status: number, text: string): never {
  let message = 'Something went wrong. Please try again.';
  let code: string | undefined;

  try {
    const body = JSON.parse(text) as { error?: string; message?: string };
    if (typeof body.message === 'string' && body.message.trim()) {
      message = body.message;
    }
    if (typeof body.error === 'string') {
      code = body.error;
    }
  } catch {
    // Ignore non-JSON error bodies — never surface raw transport text in the UI.
  }

  throw new StorefrontBffError(message, status, code);
}

function scopeQueryFromOptions(opts: StorefrontClientOptions): Record<string, string> {
  const params: Record<string, string> = {};
  if (opts.tenantId) params.tenantId = opts.tenantId;
  const storeId = opts.headers?.['X-Store-ID'] ?? opts.headers?.['x-store-id'];
  if (storeId) params.storeId = storeId;
  return params;
}

function withScopeQuery(path: string, opts: StorefrontClientOptions): string {
  const [pathname, search = ''] = path.split('?');
  const params = new URLSearchParams(search);
  for (const [key, value] of Object.entries(scopeQueryFromOptions(opts))) {
    if (!params.has(key)) params.set(key, value);
  }
  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
}

function requestHeaders(
  opts: StorefrontClientOptions,
  body: object | undefined,
  extraHeaders?: Record<string, string>,
): Record<string, string> {
  const headers: Record<string, string> = {};
  for (const [key, value] of Object.entries(opts.headers ?? {})) {
    headers[key.toLowerCase()] = value;
  }
  for (const [key, value] of Object.entries(extraHeaders ?? {})) {
    headers[key.toLowerCase()] = value;
  }

  // Public API Gateway CORS derives tenant from the host and rejects X-Tenant-ID
  // on browser preflights. Keep the header map limited to CORS-allowed values.
  delete headers['x-tenant-id'];

  if (body !== undefined) {
    headers['content-type'] = 'application/json';
  }

  return headers;
}

async function request<T>(
  opts: StorefrontClientOptions,
  method: string,
  path: string,
  body?: object,
  extraHeaders?: Record<string, string>,
): Promise<T> {
  const scopedPath = method === 'GET' ? withScopeQuery(path, opts) : path;
  const url = `${opts.baseUrl}${scopedPath}`;
  const res = await fetch(url, {
    method,
    headers: requestHeaders(opts, body, extraHeaders),
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throwStorefrontBffError(res.status, text);
  }

  return res.json() as Promise<T>;
}

/** Same as request() but always includes cookies (auth domain). */
async function credentialedRequest<T>(
  opts: StorefrontClientOptions,
  method: string,
  path: string,
  body?: object,
  extraHeaders?: Record<string, string>,
): Promise<T> {
  const url = `${opts.baseUrl}${path}`;
  const res = await fetch(url, {
    method,
    credentials: 'include',
    headers: requestHeaders(opts, body, extraHeaders),
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throwStorefrontBffError(res.status, text);
  }

  // 204 / logout-style no-content
  if (res.status === 204) return undefined as unknown as T;

  return res.json() as Promise<T>;
}

function qs(params: Record<string, string | number | boolean | string[] | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined) continue;
    if (Array.isArray(v)) {
      for (const item of v) sp.append(k, item);
    } else {
      sp.set(k, String(v));
    }
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

function collectionFilterParams(
  filters: CollectionFilters = {},
): Record<string, string | number | boolean | string[] | undefined> {
  const { options, ...rest } = filters;
  const params: Record<string, string | number | boolean | string[] | undefined> = { ...rest };
  if (options) {
    for (const [k, v] of Object.entries(options)) {
      params[`option[${k}]`] = v;
    }
  }
  return params;
}

function pricingContextParams(ctx: PricingContext): Record<string, string | undefined> {
  return { ...ctx };
}

function reviewFilterParams(filters: ReviewFilters): Record<string, string | number | boolean | undefined> {
  return { ...filters };
}

interface ApiPaymentMethodResponse {
  id: string;
  customer_email: string;
  gateway: string;
  status: 'active' | 'inactive';
  fingerprint?: string;
  instrument_descriptor?: {
    brand?: string;
    type?: string;
    last4?: string;
    exp_month?: number;
    exp_year?: number;
  };
  billing_name?: string;
  billing_email?: string;
  billing_address?: {
    first_name: string;
    last_name: string;
    email?: string;
    address1: string;
    address2?: string;
    city: string;
    state: string;
    country: string;
    zip: string;
    phone?: string;
  };
  is_default: boolean;
  audit: {
    created_at: string;
    updated_at?: string;
  };
  is_duplicate?: boolean;
}

interface ListPaymentMethodsResponse {
  data: ApiPaymentMethodResponse[];
  total: number;
  limit: number;
  offset: number;
}

function mapPaymentMethodResponse(response: ApiPaymentMethodResponse): PaymentMethodResponse {
  return {
    id: response.id,
    customerEmail: response.customer_email,
    gateway: response.gateway,
    status: response.status,
    fingerprint: response.fingerprint,
    instrumentDescriptor: response.instrument_descriptor,
    billingName: response.billing_name,
    billingEmail: response.billing_email,
    billingAddress: response.billing_address
      ? {
          firstName: response.billing_address.first_name,
          lastName: response.billing_address.last_name,
          email: response.billing_address.email,
          address1: response.billing_address.address1,
          address2: response.billing_address.address2,
          city: response.billing_address.city,
          state: response.billing_address.state,
          country: response.billing_address.country,
          zip: response.billing_address.zip,
          phone: response.billing_address.phone,
        }
      : undefined,
    isDefault: response.is_default,
    audit: {
      createdAt: response.audit.created_at,
      updatedAt: response.audit.updated_at,
    },
    isDuplicate: response.is_duplicate,
  };
}

function paymentMethodBody(input: CreatePaymentMethodRequest) {
  return {
    customer_email: input.customerEmail,
    user_uuid: input.userUuid,
    gateway: input.gateway,
    gateway_token: input.gatewayToken,
    billing_name: input.billingName,
    billing_email: input.billingEmail,
    billing_address: input.billingAddress
      ? {
          first_name: input.billingAddress.firstName,
          last_name: input.billingAddress.lastName,
          email: input.billingAddress.email,
          address1: input.billingAddress.address1,
          address2: input.billingAddress.address2,
          city: input.billingAddress.city,
          state: input.billingAddress.state,
          country: input.billingAddress.country,
          zip: input.billingAddress.zip,
          phone: input.billingAddress.phone,
        }
      : undefined,
    metadata: input.metadata,
    is_default: input.isDefault,
  };
}

export interface StorefrontClient {
  // Products
  listProducts(query?: { q?: string; page?: number; limit?: number } & PricingContext): Promise<{
    data: Product[];
    meta: { pagination: Pagination };
  }>;
  getProduct(id: string, ctx?: PricingContext): Promise<{ data: Product }>;
  getRecommendations(
    productId: string,
    query?: { limit?: number } & PricingContext,
  ): Promise<{ data: Product[] }>;

  // Collections
  listCollections(query?: { page?: number; limit?: number }): Promise<{
    data: CollectionDto[];
    meta: { pagination: Pagination };
  }>;
  getMenuCollections(): Promise<{ data: CollectionDto[] }>;
  getCollection(
    slug: string,
    filters?: CollectionFilters,
  ): Promise<{
    data: {
      collection: CollectionDto;
      products: CollectionProduct[];
      packs?: CollectionPack[];
      facets: Record<string, FacetValue[]>;
      filterDefinitions: CollectionFilterDefinition[];
    };
    meta: { pagination: Pagination };
  }>;
  searchProducts(query?: CollectionFilters): Promise<{
    data: {
      collection: CollectionDto;
      products: CollectionProduct[];
      packs?: CollectionPack[];
      facets: Record<string, FacetValue[]>;
      filterDefinitions: CollectionFilterDefinition[];
    };
    meta: { pagination: Pagination };
  }>;

  // Page bundles
  getCollectionPage(slug: string, filters?: CollectionFilters): Promise<CollectionPageResponse>;
  getHomePage(query?: { locale?: string }): Promise<HomePageResponse>;
  getGlobalChrome(query?: { locale?: string }): Promise<GlobalChromeResponse>;
  getProductPage(slug: string, ctx?: PricingContext): Promise<ProductPageResponse>;
  getPackPage(slug: string, ctx?: PricingContext): Promise<PackPageResponse>;
  getPackById(id: string, ctx?: PricingContext): Promise<PackSummaryResponse>;

  // Reviews
  listReviews(
    productId: string,
    filters?: ReviewFilters,
  ): Promise<{
    data: Review[];
    meta: {
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
      stats?: ReviewStats;
    };
  }>;
  getReviewStats(productId: string): Promise<{ data: ReviewStats }>;
  createReview(input: CreateReviewInput): Promise<{ data: Review }>;
  voteReview(
    reviewId: string,
    voteType: 'helpful' | 'not_helpful',
  ): Promise<{ data: Review }>;

  // Carts
  getCart(token: string): Promise<{ data: Cart }>;
  createCart(input: CreateCartInput): Promise<{ data: Cart }>;
  updateCart(token: string, input: UpdateCartInput): Promise<{ data: Cart }>;
  removeCartItem(token: string, input: RemoveCartItemInput, idempotencyKey: string): Promise<{ data: Cart }>;
  addPackToCart(token: string, input: AddPackToCartInput): Promise<{ data: Cart }>;

  // Pricing
  getFreeShippingThreshold(currency?: string): Promise<{
    data: { threshold: number | null; currency: string };
  }>;
  quote(items: QuoteItem[], context: PricingContext & { currency: string }): Promise<{ data: QuoteResult }>;
  resolvePricesBatch(
    items: BatchResolveItem[],
    context: PricingContext & { currency: string },
  ): Promise<{ data: BatchResolveResult }>;
  resolveProductPrice(query: {
    product_id: string;
    variant_id?: string;
    quantity?: number;
  } & PricingContext &
    { currency: string }): Promise<{ data: ResolveProductPriceResult }>;
  getCreditsConfig(currency?: string): Promise<{ data: CreditsConfig }>;
  getCreditsBalance(userId: string): Promise<{ data: CreditsBalance }>;
  getCreditsTransactions(userId: string): Promise<{ data: CreditsTransactions }>;
  getUserCoupons(userId: string): Promise<{ data: CustomerCoupons }>;

  // Auth — cookies managed by BFF (HttpOnly access_token)
  login(input: LoginRequest): Promise<AuthResponse>;
  register(input: RegisterRequest): Promise<AuthResponse>;
  loginWithGoogle(input: GoogleAuthRequest): Promise<AuthResponse>;
  refresh(): Promise<AuthResponse>;
  logout(): Promise<void>;
  getMe(): Promise<{ data: UserProfile }>;
  requestMagicLink(input: MagicLinkRequest): Promise<MagicLinkRequestResponse>;
  verifyMagicLink(input: MagicLinkVerifyRequest): Promise<MagicLinkVerifyResponse>;
  updateProfile(input: UpdateProfileRequest): Promise<{ data: UserProfile }>;

  // Users — shipping addresses (credentialed; requires active BFF session cookie)
  getShippingAddresses(): Promise<{ data: ShippingAddress[] }>;
  getShippingAddress(id: string): Promise<{ data: ShippingAddress }>;
  createShippingAddress(input: CreateShippingAddressInput): Promise<{ data: ShippingAddress }>;
  updateShippingAddress(id: string, input: UpdateShippingAddressInput): Promise<{ data: ShippingAddress }>;
  deleteShippingAddress(id: string): Promise<void>;

  // Payments
  listPaymentMethods(params: { customerEmail?: string; userUuid?: string }): Promise<PaymentMethodResponse[]>;
  createPaymentMethod(input: CreatePaymentMethodRequest, idempotencyKey: string): Promise<PaymentMethodResponse>;

  // Orders — identity comes from the BFF session cookie / JWT, not query params
  listOrders(query?: {
    page?: number;
    limit?: number;
  }): Promise<{ data: Order[]; meta: { pagination: OrderPagination } }>;
  getOrder(orderId: string): Promise<{ data: Order }>;
  refundOrder(orderId: string, body: RefundOrderRequest): Promise<{ data: unknown }>;

  // Subscriptions — customer id comes from the BFF session JWT, not path params
  getCustomerSubscriptions(): Promise<{ data: CustomerSubscription[] }>;
  cancelSubscription(subscriptionId: string, body: { reason: string }): Promise<void>;
  pauseSubscription(subscriptionId: string, body?: { reason?: string }): Promise<void>;
  resumeSubscription(subscriptionId: string): Promise<void>;
  skipSubscription(subscriptionId: string, body?: { reason?: string }): Promise<void>;
  rescheduleSubscription(subscriptionId: string, body: { new_next_charge_date: string }): Promise<void>;
  updateSubscriptionItems(subscriptionId: string, body: { items: SubscriptionItemUpdate[] }): Promise<{ data: CustomerSubscription }>;

  // Leads / newsletter
  captureLead(input: {
    email: string;
    locale?: string;
    funnel_id?: string;
    step_id?: string;
    form_id?: string;
    lead_source?: string;
  }): Promise<{ accepted: true; lead_ref: string }>;
}

export function createStorefrontClient(opts: StorefrontClientOptions): StorefrontClient {
  const r = <T>(method: string, path: string, body?: object, headers?: Record<string, string>) =>
    request<T>(opts, method, path, body, headers);
  const cr = <T>(method: string, path: string, body?: object, headers?: Record<string, string>) =>
    credentialedRequest<T>(opts, method, path, body, headers);

  return {
    // ---- Products ----
    listProducts(query = {}) {
      const { currency, country, ...rest } = query;
      const params: Record<string, string | number | boolean | string[] | undefined> = {
        ...rest,
        ...pricingContextParams({ currency, country }),
      };
      return r('GET', `/v1/storefront/products${qs(params)}`);
    },
    getProduct(id, ctx = {}) {
      return r('GET', `/v1/storefront/products/${encodeURIComponent(id)}${qs(pricingContextParams(ctx))}`);
    },
    getRecommendations(productId, query = {}) {
      const { currency, country, ...rest } = query;
      const params: Record<string, string | number | boolean | string[] | undefined> = {
        ...rest,
        ...pricingContextParams({ currency, country }),
      };
      return r(
        'GET',
        `/v1/storefront/products/${encodeURIComponent(productId)}/recommendations${qs(params)}`,
      );
    },

    // ---- Collections ----
    listCollections(query = {}) {
      return r('GET', `/v1/storefront/collections${qs(query)}`);
    },
    getMenuCollections() {
      return r('GET', '/v1/storefront/collections/menu');
    },
    getCollection(slug, filters = {}) {
      return r('GET', `/v1/storefront/collections/${encodeURIComponent(slug)}${qs(collectionFilterParams(filters))}`);
    },
    searchProducts(query = {}) {
      return r('GET', `/v1/storefront/collections/search${qs(collectionFilterParams(query))}`);
    },

    // ---- Page bundles ----
    getCollectionPage(slug, filters = {}) {
      return r('GET', `/v1/storefront/pages/collection/${encodeURIComponent(slug)}${qs(collectionFilterParams(filters))}`);
    },
    getHomePage(query = {}) {
      return r('GET', `/v1/storefront/pages/home${qs(query)}`);
    },
    getGlobalChrome(query = {}) {
      return r('GET', `/v1/storefront/pages/global${qs(query)}`);
    },
    getProductPage(slug, ctx = {}) {
      return r('GET', `/v1/storefront/pages/product/${encodeURIComponent(slug)}${qs(pricingContextParams(ctx))}`);
    },
    getPackPage(slug, ctx = {}) {
      return r('GET', `/v1/storefront/pages/pack/${encodeURIComponent(slug)}${qs(pricingContextParams(ctx))}`);
    },
    getPackById(id, ctx = {}) {
      return r('GET', `/v1/storefront/packs/${encodeURIComponent(id)}${qs(pricingContextParams(ctx))}`);
    },

    // ---- Reviews ----
    listReviews(productId, filters = {}) {
      return r('GET', `/v1/storefront/products/${encodeURIComponent(productId)}/reviews${qs(reviewFilterParams(filters))}`);
    },
    getReviewStats(productId) {
      return r('GET', `/v1/storefront/products/${encodeURIComponent(productId)}/reviews/stats`);
    },
    createReview(input) {
      return r('POST', '/v1/storefront/reviews', input);
    },
    voteReview(reviewId, voteType) {
      return r('POST', `/v1/storefront/reviews/${encodeURIComponent(reviewId)}/votes`, { voteType });
    },

    // ---- Carts ----
    getCart(token) {
      return r('GET', `/v1/storefront/carts/${encodeURIComponent(token)}`);
    },
    createCart(input) {
      return r('POST', '/v1/storefront/carts', input);
    },
    updateCart(token, input) {
      return r('PUT', `/v1/storefront/carts/${encodeURIComponent(token)}`, input);
    },
    removeCartItem(token, input, idempotencyKey) {
      return r('POST', `/v1/storefront/carts/${encodeURIComponent(token)}/items/remove`, input, {
        'Idempotency-Key': idempotencyKey,
      });
    },
    addPackToCart(token, input) {
      return r('POST', `/v1/storefront/carts/${encodeURIComponent(token)}/packs`, input);
    },

    // ---- Pricing ----
    getFreeShippingThreshold(currency = 'USD') {
      return r('GET', `/v1/storefront/pricing/free-shipping-threshold${qs({ currency })}`);
    },
    quote(items, context) {
      return r('POST', '/v1/storefront/pricing/quote', {
        items,
        context: {
          currency: context.currency ?? 'USD',
          country: context.country,
          customer_groups: context.customer_groups?.split(',').map((s) => s.trim()).filter(Boolean),
          channel: context.channel,
        },
      });
    },
    resolvePricesBatch(items, context) {
      return r('POST', '/v1/storefront/pricing/resolve/batch', {
        items,
        context: {
          currency: context.currency ?? 'USD',
          country: context.country,
          customer_groups: context.customer_groups?.split(',').map((s) => s.trim()).filter(Boolean),
          channel: context.channel,
        },
      });
    },
    resolveProductPrice(query) {
      const { product_id, variant_id, quantity, ...ctx } = query;
      return r(
        'GET',
        `/v1/storefront/pricing/product${qs({
          product_id,
          variant_id,
          quantity,
          ...pricingContextParams(ctx),
        })}`,
      );
    },

    // ---- Auth ----
    login(input) {
      return cr('POST', '/v1/storefront/auth/login', input);
    },
    register(input) {
      return cr('POST', '/v1/storefront/auth/register', input);
    },
    loginWithGoogle(input) {
      return cr('POST', '/v1/storefront/auth/google', input);
    },
    refresh() {
      return cr('POST', '/v1/storefront/auth/refresh', {});
    },
    logout() {
      return cr('POST', '/v1/storefront/auth/logout', {});
    },
    getMe() {
      return cr('GET', '/v1/storefront/auth/me');
    },
    requestMagicLink(input) {
      return cr('POST', '/v1/storefront/auth/magic-link/request', input);
    },
    verifyMagicLink(input) {
      return cr('POST', '/v1/storefront/auth/magic-link/verify', input);
    },
    updateProfile(input) {
      return cr('PATCH', '/v1/storefront/users/me/profile', input);
    },

    // ---- Users ----
    getShippingAddresses() {
      return cr('GET', '/v1/storefront/users/me/shipping-addresses');
    },
    getShippingAddress(id) {
      return cr('GET', `/v1/storefront/users/me/shipping-addresses/${encodeURIComponent(id)}`);
    },
    createShippingAddress(input) {
      return cr('POST', '/v1/storefront/users/me/shipping-addresses', input);
    },
    updateShippingAddress(id, input) {
      return cr('PATCH', `/v1/storefront/users/me/shipping-addresses/${encodeURIComponent(id)}`, input);
    },
    deleteShippingAddress(id) {
      return cr('DELETE', `/v1/storefront/users/me/shipping-addresses/${encodeURIComponent(id)}`);
    },

    // ---- Payments ----
    async listPaymentMethods(params) {
      const response = await r<ListPaymentMethodsResponse>(
        'GET',
        `/v1/storefront/payments/payment-methods${qs({
          customer_email: params.customerEmail,
          user_uuid: params.userUuid,
          status: 'active',
        })}`,
      );
      return response.data.map(mapPaymentMethodResponse);
    },
    async createPaymentMethod(input, idempotencyKey) {
      const response = await r<{ data: ApiPaymentMethodResponse }>(
        'POST',
        '/v1/storefront/payments/payment-methods',
        paymentMethodBody(input),
        { 'Idempotency-Key': idempotencyKey },
      );
      return mapPaymentMethodResponse(response.data);
    },

    // ---- Orders ----
    listOrders(query = {}) {
      return cr('GET', `/v1/storefront/orders${qs(query)}`);
    },
    getOrder(orderId) {
      return cr('GET', `/v1/storefront/orders/${encodeURIComponent(orderId)}`);
    },
    refundOrder(orderId, body) {
      return cr('POST', `/v1/storefront/orders/${encodeURIComponent(orderId)}/refunds`, body);
    },

    // ---- Subscriptions ----
    getCustomerSubscriptions() {
      return cr('GET', '/v1/storefront/subscriptions');
    },
    cancelSubscription(subscriptionId, body) {
      return cr(
        'POST',
        `/v1/storefront/subscriptions/${encodeURIComponent(subscriptionId)}/cancel`,
        body
      );
    },
    pauseSubscription(subscriptionId, body = {}) {
      return cr(
        'POST',
        `/v1/storefront/subscriptions/${encodeURIComponent(subscriptionId)}/pause`,
        body
      );
    },
    resumeSubscription(subscriptionId) {
      return cr(
        'POST',
        `/v1/storefront/subscriptions/${encodeURIComponent(subscriptionId)}/resume`
      );
    },
    skipSubscription(subscriptionId, body = {}) {
      return cr(
        'POST',
        `/v1/storefront/subscriptions/${encodeURIComponent(subscriptionId)}/skip`,
        body
      );
    },
    rescheduleSubscription(subscriptionId, body) {
      return cr(
        'PUT',
        `/v1/storefront/subscriptions/${encodeURIComponent(subscriptionId)}/reschedule`,
        body
      );
    },
    updateSubscriptionItems(subscriptionId, body) {
      return cr(
        'PUT',
        `/v1/storefront/subscriptions/${encodeURIComponent(subscriptionId)}/items`,
        body
      );
    },

    // Credits
    getCreditsConfig(currency: string = 'USD') {
      return cr('GET', `/v1/storefront/credits/config?currency=${encodeURIComponent(currency)}`);
    },
    getCreditsBalance(userId: string) {
      return cr('GET', `/v1/storefront/credits/balance/${encodeURIComponent(userId)}`);
    },
    getCreditsTransactions(userId: string) {
      return cr('GET', `/v1/storefront/credits/transactions/${encodeURIComponent(userId)}`);
    },

    // Coupons
    getUserCoupons(userId: string) {
      return cr('GET', `/v1/storefront/coupons/customers/${encodeURIComponent(userId)}`);
    },

    // Leads / newsletter
    captureLead(input) {
      return r('POST', '/v1/storefront/leads', input);
    },
  };
}
